import StageCellEditor from './StageCellEditor';
import StrategyCellEditor from './StrategyCellEditor';
import TagCellEditor from '../tags/TagCellEditor';
import { PipelinePinIcon } from '../../../assets/pipeline/PipelinePinIcon';
import { getOwnerInitials } from '../utils.js';

// SOW 4 A1a: widths from the `pipeline` frame at 1440, summing to the 1200 table with the 42px
// checkbox column (PipelineGrid.jsx); Property takes the remainder (350). spec.md a2.
export const PIPELINE_COLUMN_WIDTHS = {
  stage: 172,
  strategy: 168,
  tagsDisplay: 304,
  createdAtDisplay: 116,
  pinned: 48,
};

// Owner is off the grid since SOW 4 ("preserve its code, it will return for Ultimate users"):
// nothing passes showOwner today.
const ownerColumn = (user) => ({
  // 56 = the "Owner" header at 14/700 (44px measured) + the 12px gutter; the 30px avatar is narrower.
  field: "owner",
  headerName: "Owner",
  width: 56,
  sortable: false,

  renderCell: (params) => (
    <div
      className="flex items-center justify-center rounded-full text-white"
      style={{ width: 30, height: 30, backgroundColor: '#5F6B5B' }}
    >
      <span style={{ fontSize: 11 }}>
        {getOwnerInitials(params.row.raw?.createdBy, user)}
      </span>
    </div>
  ),
});

export const getPipelineColumns = ({ activeStage, onStageUpdate, onStrategyUpdate, allTags, onToggleTag, onManageTags, onNavigate, onPinToggle, onSaveOk, onSaveFailed, user, showOwner = false }) => [
  {
    field: "property",
    headerName: "Property",
    flex: 1,
    sortable: false,

    renderCell: (params) => {
      // Non-blank rather than truthy: a whitespace-only street would draw its own run and the separator.
      const hasStreet = Boolean(params.row.streetDisplay && String(params.row.streetDisplay).trim())
      return (
        // pr-[12px]: the grid-wide zero cell padding (PipelineGrid.jsx) leaves no gap before Stage's pill.
        // No title: SOW 4 removed the address tooltip ("it just repeats the address").
        <div
          className="flex flex-col justify-center leading-[1.2] cursor-pointer py-[16px] pr-[12px] w-[100%] group"
          onClick={() => onNavigate?.(params.row.id)}
        >
          {/* SOW 4 A1c: street 14/700, city/state/zip 14/400, line 2 13/400, all #17270D; line 1 hovers
              #328C12. The hover colour sits on the outer span too: it generates the ellipsis. */}
          <span className="text-[14px] text-brand-dark truncate group-hover:text-brand-hover transition-colors">
            {hasStreet ? (
              <span className="font-bold group-hover:text-brand-hover transition-colors">
                {params.row.streetDisplay}
              </span>
            ) : null}
            {params.row.cityStateZip ? (
              <>
                {hasStreet ? ' ' : null}
                <span className="text-[14px] font-normal text-brand-dark group-hover:text-brand-hover transition-colors">
                  {params.row.cityStateZip}
                </span>
              </>
            ) : null}
          </span>

          <span className="mt-[2px] text-[13px] font-normal text-brand-dark truncate">
            {params.row.propertySummary}
          </span>
        </div>
      );
    },
  },
  {
    field: "stage",
    headerName: "Stage",
    width: PIPELINE_COLUMN_WIDTHS.stage,
    sortable: false,

    renderCell: (params) => (
      <StageCellEditor
        params={params}
        activeStage={activeStage}
        onStageUpdate={onStageUpdate}
        onSaveOk={onSaveOk}
        onSaveFailed={onSaveFailed}
      />
    ),
  },

  {
    field: "strategy",
    headerName: "Strategy",
    width: PIPELINE_COLUMN_WIDTHS.strategy,
    sortable: false,

    renderCell: (params) => (
      <StrategyCellEditor
        params={params}
        onStrategyUpdate={onStrategyUpdate}
        onSaveOk={onSaveOk}
        onSaveFailed={onSaveFailed}
      />
    ),
  },

  ...(showOwner ? [ownerColumn(user)] : []),

  {
    field: "tagsDisplay",
    headerName: "Tags",
    width: PIPELINE_COLUMN_WIDTHS.tagsDisplay,
    sortable: false,

    renderCell: (params) => (
      <TagCellEditor
        params={params}
        allTags={allTags}
        onToggleTag={onToggleTag}
        onManageTags={onManageTags}
      />
    ),
  },

  {
    // Header and value centred on the column, as drawn (dates centred on rel. 1094).
    field: "createdAtDisplay",
    headerName: "Added",
    width: PIPELINE_COLUMN_WIDTHS.createdAtDisplay,
    align: 'center',
    headerAlign: 'center',
    sortable: true,

    // Sort on the timestamp: the display string "6/10/26" collates ahead of "6/4/26".
    valueGetter: (_value, row) => {
      const time = new Date(row.raw?.createdAt).getTime();
      return Number.isNaN(time) ? 0 : time;
    },
    // Ctrl+C cell copy serialises through valueFormatter (useGridClipboard); reuse the display string.
    valueFormatter: (_value, row) => row.createdAtDisplay,

    renderCell: (params) => (
      <span className="text-[13px] text-brand-dark truncate">{params.row.createdAtDisplay}</span>
    ),
  },

  {
    field: "pinned",
    headerName: "",
    width: PIPELINE_COLUMN_WIDTHS.pinned,
    // MUI clamps any width below its default minWidth (50) up to 50, taking the difference off Property.
    minWidth: PIPELINE_COLUMN_WIDTHS.pinned,
    sortable: false,
    disableColumnMenu: true,
    // The pin's 14x14 box sits 20.5 from the table's right edge (Comment Set #1, Pipeline 3.1).
    align: 'right',
    headerAlign: 'center',

    renderCell: ({ row }) => (
      <button
        onClick={(e) => {
          e.stopPropagation()
          // The save (PATCH, generation gate, rollback) lives in Pipeline.jsx's handlePinToggle.
          onPinToggle?.(row.id, !row.pinned)
        }}
        style={{ marginRight: 20.5 }}
        className="group/pin flex items-center justify-center"
        title={row.pinned ? 'Unpin' : 'Pin to top'}
      >
        {/* SOW 4: "Update pin hover to be the pinned state" -- the head fills under the pointer. */}
        <PipelinePinIcon active={row.pinned} headClassName="group-hover/pin:fill-[#4F5F4A]" />
      </button>
    ),
  },
];
